// cherry-pick lodash methods (the right way). DO NOT use import from 'lodash'.
import concat from 'lodash/concat';
import find from 'lodash/find';
import isObject from 'lodash/isObject';
import without from 'lodash/without';
import React, { Component, Fragment } from 'react';
import { SegmentEventTypes } from '../data/tracking';
// import Error module
import Error from '../error';
import Analytics from '../utils/Analytics';
import { DirtyStateModal } from './DirtyStateModal.component';
import { ErrorModal } from './ErrorModal.component';
import { MessageModal } from './MessageModal.component';
// import Modal context
import { ModalContext } from './ModalContext';
import TutorialModal from './TutorialModal.component';

const { ErrorService } = Error;

let globalIdCounter = 0;
const getNextModalId = () => {
	globalIdCounter += 1;
	return globalIdCounter;
};

export class ModalContainer extends Component {
	constructor(props) {
		super(props);

		this.state = {
			// List of all modal components
			modals: [],

			// Display a modal.
			showModal: ({ component: ModalComponent, data, closeCallback }) => {
				const modalId = getNextModalId();

				const newModal = {
					data,
					modalId,
					closeCallback,

					// add additional prop: modalId to the react component
					component: <ModalComponent data={data || {}} modalId={modalId} />,
				};

				this.setState(previousState => ({
					modals: concat(previousState.modals, newModal),
				}));

				// return the unique to the caller in case the caller wants to close the modal
				return modalId;
			},

			// Display an error modal.
			showError: ({ data, response, closeCallback }) => {
				const { modals, showModal } = this.state;

				// translate the response object received from Communication class
				const errorData = isObject(response)
					? {
							status: response.status,
							code: response.ErrorCode,
							description: response.ErrorDescription,
							url: response.url,
						}
					: data;
				const error = ErrorService.process(errorData);

				// check whether the error has been showing
				const errorExist = find(modals, { data: error });

				// display the error modal
				if (!errorExist && error) {
					Analytics.trackSegmentEvent({
						trackingData: {
							error_type: 'dialog_error',
							error_code: error.title || `Error ${error.code || ''}`,
							error_user_message: error.description || '',
						},
						eventType: SegmentEventTypes.SystemErrorMessage,
					});

					showModal({
						component: ErrorModal,
						data: error,
						closeCallback,
					});
				}
			},

			// Display a message modal.
			showMessage: ({ data, closeCallback }) => {
				const { modals, showModal } = this.state;

				// check whether the modal is already displayed
				const modalExists = find(modals, { data });

				// display the modal
				if (!modalExists && data) {
					showModal({
						component: MessageModal,
						data,
						closeCallback,
					});
				}
			},

			// Display a warning modal if the page's form state is 'dirty'.
			// Users will get a confirmation dialog when navigating away from a dirty state.
			showDirtyStateModal: ({ data }) => {
				const { modals, showModal } = this.state;

				// check whether the modal is already displayed
				const modalExists = find(modals, { data });

				// display the modal
				if (!modalExists && data) {
					showModal({
						component: DirtyStateModal,
						data,
					});
				}
			},

			// Display a Tutorial modal.
			// was not updated to functional setState so may have race conditions.
			// https://stamps.atlassian.net/wiki/spaces/RnD/pages/460128678/Tutorial#showTutorial
			// @param {object} See 'ShowTutorial' in Tutorial tech spec.
			showTutorial: ({ id, guide, step, onShow, onEnd }) => {
				const { modals, idCounter } = { ...this.state };

				// check whether the modal is already displayed
				const existingModal = find(modals, { id });
				const modalId = existingModal ? existingModal.modalId : idCounter + 1;

				if (!existingModal) {
					this.setState({
						modals: concat(modals, {
							id,
							guide,
							modalId,

							// add additional prop: modalId to the react component
							component: (
								<TutorialModal
									id={id}
									guide={guide}
									step={step}
									modalId={modalId}
									onEnd={onEnd}
									onShow={onShow}
								/>
							),
						}),
						idCounter: modalId,
					});
				}

				return modalId;
			},

			// Close a Tutorial modal.
			// @param modalId The assigned unique id to the modal.
			// @param callback The callback after the modal is destroyed.
			closeTutorial: (modalId, callback) => {
				const { modals } = { ...this.state };

				// check whether the modal exists
				const modal = find(modals, { modalId });

				if (modal) {
					// remove the tutorial modal from the pool
					this.setState(
						{
							modals: without(modals, modal),
						},
						callback
					);
				}
			},

			// Close a modal.
			// @param modalId The assigned unique id to the modal.
			// @param closeType The type of button clicked to trigger the close action.
			// @param closeData Additional data from modal.
			closeModal: (modalId, closeType, modalData) => {
				let removedCloseCallback;
				this.setState(
					prevState => {
						const modal = find(prevState.modals, { modalId });
						if (!modal) {
							console.warn(
								`[ModalContainer] closeModal: No modal found with id: ${modalId}`
							);
							return prevState;
						} else {
							removedCloseCallback = modal.closeCallback;
							const nextModals = without(prevState.modals, modal);
							return {
								modals: nextModals,
							};
						}
					},
					() => {
						if (removedCloseCallback) {
							removedCloseCallback(closeType, modalData);
						}
					}
				);
			},

			// Close all modals
			closeAll: callback => {
				this.setState(
					{
						modals: [],
					},
					callback
				);
			},
		};
	}

	render() {
		const { modals } = this.state;
		const { children } = this.props;

		return (
			<ModalContext.Provider value={this.state}>
				{children}

				{modals.map(({ modalId, component }) => (
					<Fragment key={modalId}>{component}</Fragment>
				))}
			</ModalContext.Provider>
		);
	}
}
